import React, { useState } from "react";
import { useReveal } from "@/lib/useReveal";
import {
  Store,
  Rocket,
  Briefcase,
  Stethoscope,
  GraduationCap,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Zap,
  Check,
} from "lucide-react";

/**
 * Bold redesign - a full-width gradient spotlight card that swaps
 * mesh-gradient theme, copy, and floating signal chips per persona.
 * Persona selector is a gradient-glow tab bar below.
 */
const PERSONAS = [
  {
    key: "d2c",
    tab: "D2C",
    label: "D2C founders",
    icon: Store,
    tagline: "Beauty · Skincare · Home · Food",
    title: "Every scroll becomes a shortlist.",
    subtitle:
      "Meet buyers the moment they complain about a white cast, a chunky texture, or a delayed order.",
    perks: [
      "Skip the ad tax - reach buyers in threads, not auctions.",
      "Every result ships with intent, ICP match & reply angle.",
    ],
    // Mesh gradient theme per persona
    mesh:
      "radial-gradient(1200px 500px at 15% 10%, #F5A48A 0%, transparent 55%), radial-gradient(900px 500px at 100% 20%, #F0BE99 0%, transparent 55%), radial-gradient(700px 400px at 80% 100%, #D95A40 0%, transparent 60%), linear-gradient(135deg, #F9D8C4 0%, #F2B592 100%)",
    accent: "#B0402A",
    accentSoft: "#D95A40",
    chips: [
      { source: "Reddit", handle: "r/IndianSkincare", text: "Any recs for a sunscreen without white cast? Budget under ₹800.", score: 94, intent: "Buying" },
      { source: "Forums", handle: "Forum user · Bengaluru", text: "Every korean sunscreen I try feels sticky. Any Indian brand that works?", score: 88, intent: "Comparison" },
      { source: "Reddit", handle: "r/AsianBeauty", text: "Trying to build a barrier-safe routine - would love alternatives.", score: 76, intent: "Discovery" },
    ],
  },
  {
    key: "saas",
    tab: "SaaS",
    label: "SaaS founders",
    icon: Rocket,
    tagline: "B2B · CRM · DevTools · AI",
    title: "Find switchers before your competitor does.",
    subtitle:
      "Catch the moment someone says 'Salesforce is overkill' or 'looking to move off Freshworks.'",
    perks: [
      "Ranked by opportunity - 90+ leads bubble to the top.",
      "Every lead ships with intent, ICP match & matched keyword.",
    ],
    mesh:
      "radial-gradient(1200px 500px at 10% 10%, #9DC183 0%, transparent 55%), radial-gradient(900px 500px at 100% 30%, #A9CDA0 0%, transparent 55%), radial-gradient(700px 400px at 85% 100%, #1F4A38 0%, transparent 60%), linear-gradient(135deg, #C8DFBF 0%, #A3C99A 100%)",
    accent: "#153328",
    accentSoft: "#1F4A38",
    chips: [
      { source: "Reddit", handle: "r/CRM", text: "Salesforce is overkill for our 12-person team. Fair mid-market alternative?", score: 96, intent: "Switch" },
      { source: "LinkedIn", handle: "Head of Ops, SaaS · Mumbai", text: "Re-evaluating our GTM stack. Any Indian CRMs founders swear by?", score: 91, intent: "Buyer" },
      { source: "Forums", handle: "Forum user · Indie builder", text: "HubSpot pricing scaled 3x this year. Time to look at cheaper alts.", score: 84, intent: "Churn" },
    ],
  },
  {
    key: "agencies",
    tab: "Agencies",
    label: "Marketing agencies",
    icon: Briefcase,
    tagline: "Growth · Performance · Content",
    title: "Client-ready lead lists in an afternoon.",
    subtitle:
      "Run parallel scans across every client brand and prove ROI in your very first review.",
    perks: [
      "Isolated workspaces per client - scans, usage, ledgers.",
      "Export a ranked list, save to kanban, share one link.",
    ],
    mesh:
      "radial-gradient(1200px 500px at 10% 10%, #98AEE4 0%, transparent 55%), radial-gradient(900px 500px at 100% 20%, #B2A8DC 0%, transparent 55%), radial-gradient(700px 400px at 90% 100%, #3B4EA5 0%, transparent 60%), linear-gradient(135deg, #B8C7EA 0%, #9AA8DE 100%)",
    accent: "#2A3A85",
    accentSoft: "#3B4EA5",
    chips: [
      { source: "Reddit", handle: "r/IndianStartups", text: "Need a performance marketing agency that gets D2C. Recs?", score: 92, intent: "Buyer" },
      { source: "LinkedIn", handle: "D2C founder · Delhi", text: "Looking to outsource paid social for the next 6 months.", score: 89, intent: "Buyer" },
      { source: "Reddit", handle: "r/marketing", text: "Fired our last agency. What green flags to look for this time?", score: 78, intent: "Warm" },
    ],
  },
  {
    key: "local",
    tab: "Local",
    label: "Local services",
    icon: Stethoscope,
    tagline: "Clinics · Studios · Contractors",
    title: "Clinics & studios that book customers today.",
    subtitle:
      "The founder is the marketer - Leadmato does the outreach research for you, one query at a time.",
    perks: [
      "City + intent context - 'dentist in HSR', 'yoga Andheri'.",
      "Type. Scan. Reply. Zero dashboards to babysit.",
    ],
    mesh:
      "radial-gradient(1200px 500px at 10% 10%, #E0A85A 0%, transparent 55%), radial-gradient(900px 500px at 100% 20%, #D9A05C 0%, transparent 55%), radial-gradient(700px 400px at 85% 100%, #B8813A 0%, transparent 60%), linear-gradient(135deg, #EACB92 0%, #DFB273 100%)",
    accent: "#8A5F26",
    accentSoft: "#B8813A",
    chips: [
      { source: "Reddit", handle: "r/bangalore", text: "Good dentists in HSR / Koramangala for a root canal? Budget conscious.", score: 95, intent: "Buyer" },
      { source: "Forums", handle: "Forum user · HSR resident", text: "Yoga studio recs in HSR? Beginner-friendly, not too aggressive.", score: 82, intent: "Discovery" },
      { source: "Reddit", handle: "r/IndiaTravel", text: "Need a physio in Bangalore who understands running injuries.", score: 74, intent: "Warm" },
    ],
  },
  {
    key: "coaches",
    tab: "Coaches",
    label: "Coaches & Ed-Tech",
    icon: GraduationCap,
    tagline: "Cohorts · Courses · Bootcamps",
    title: "Fill every cohort without burning ad spend.",
    subtitle:
      "Meet learners the second they ask for recommendations, before marketplace ads see them.",
    perks: [
      "Filter by learner intent, price band & timeline in seconds.",
      "Save the best leads to a kanban and track every follow-up.",
    ],
    mesh:
      "radial-gradient(1200px 500px at 10% 10%, #B48ADB 0%, transparent 55%), radial-gradient(900px 500px at 100% 20%, #C994B8 0%, transparent 55%), radial-gradient(700px 400px at 90% 100%, #7A3EA5 0%, transparent 60%), linear-gradient(135deg, #D3B4E5 0%, #B891CE 100%)",
    accent: "#552878",
    accentSoft: "#7A3EA5",
    chips: [
      { source: "Reddit", handle: "r/india", text: "Looking for a PM bootcamp with actual placement support. Budget ₹80k.", score: 97, intent: "Buyer" },
      { source: "LinkedIn", handle: "Analyst · career switcher", text: "Transitioning into product this year - which Jan '26 cohort?", score: 90, intent: "Buyer" },
      { source: "Forums", handle: "Forum user · dev learning AI", text: "Any online AI course with cohort-style learning that isn't $2000?", score: 81, intent: "Comparison" },
    ],
  },
];

const SOURCE_DOT = { Reddit: "#FF4500", Forums: "#1F4A38", LinkedIn: "#0A66C2" };

// Real-brand logo marks (compact SVGs so the deck reads authentic).
function SourceLogo({ source, className = "w-5 h-5" }) {
  if (source === "Reddit") {
    return (
      <svg viewBox="0 0 40 40" className={className} aria-label="Reddit">
        <circle cx="20" cy="20" r="20" fill="#FF4500" />
        <path
          fill="#fff"
          d="M31 20a3 3 0 0 0-5.1-2.1c-1.8-1.2-4.2-2-6.8-2.1l1.4-4.4 3.6.8v.1a2 2 0 1 0 .7-1.6l-4.2-.9a.5.5 0 0 0-.6.3l-1.6 5a12 12 0 0 0-6.8 2A3 3 0 1 0 9 20.4c0 .3-.1.6-.1.9 0 4.4 5 8 11.1 8s11.1-3.6 11.1-8c0-.3 0-.6-.1-1 1.1-.5 1.9-1.6 1.9-2.9zm-17.4 2.5a2 2 0 1 1 4 0 2 2 0 0 1-4 0zm10 5.1c-1.3.9-3 1.4-4.6 1.4s-3.3-.4-4.6-1.4a.5.5 0 1 1 .6-.8c1 .7 2.5 1.1 4 1.1s3-.4 4-1.1a.5.5 0 1 1 .6.8zm-.3-3a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"
        />
      </svg>
    );
  }
  if (source === "Forums") {
    return (
      <svg viewBox="0 0 40 40" className={className} aria-label="Forums">
        <rect width="40" height="40" rx="8" fill="#1F4A38" />
        <path
          fill="#fff"
          d="M11 13.5A2.5 2.5 0 0 1 13.5 11h9A2.5 2.5 0 0 1 25 13.5v5A2.5 2.5 0 0 1 22.5 21H17l-4 3v-3h-.5A1.5 1.5 0 0 1 11 19.5v-6z"
          opacity="0.95"
        />
        <path
          fill="#8FD3B6"
          d="M27 17h1.5A2.5 2.5 0 0 1 31 19.5v6a1.5 1.5 0 0 1-1.5 1.5H29v3l-4-3h-5A2.5 2.5 0 0 1 17.6 25H22.5a4.5 4.5 0 0 0 4.5-4.5V17z"
        />
      </svg>
    );
  }
  if (source === "LinkedIn") {
    return (
      <svg viewBox="0 0 40 40" className={className} aria-label="LinkedIn">
        <rect width="40" height="40" rx="8" fill="#0A66C2" />
        <path
          fill="#fff"
          d="M13.5 15.5v14h-4v-14h4zM11.5 8a2.4 2.4 0 1 1 0 4.8 2.4 2.4 0 0 1 0-4.8zM31 22.4v7.1h-4v-6.6c0-1.6-.6-2.7-2.1-2.7-1.1 0-1.8.7-2.1 1.5-.1.3-.2.6-.2 1v6.8h-4v-14h4v1.9c.5-.9 1.5-2.2 3.7-2.2 2.7 0 4.7 1.8 4.7 5.2z"
        />
      </svg>
    );
  }
  return null;
}

function SignalChip({ chip, idx, accent, accentSoft }) {
  return (
    <div
      className="relative"
      style={{
        animation: `lm-count-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${idx * 90}ms both`,
      }}
    >
      <div
        className="relative rounded-lg bg-white/95 backdrop-blur-sm p-4 shadow-[0_18px_40px_-24px_rgba(0,0,0,0.35)]"
        style={{ boxShadow: `0 18px 40px -20px ${accent}55` }}
      >
        {/* Top row: logo + source + handle + score */}
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <SourceLogo source={chip.source} className="w-5 h-5 shrink-0" />
            <span className="text-[12px] font-semibold text-lm-text">{chip.source}</span>
            <span className="text-[12px] text-lm-text3 truncate">· {chip.handle}</span>
          </div>
          <div
            className="flex items-center gap-1 text-[10px] font-mono font-semibold px-2 py-0.5 rounded-md text-white shrink-0"
            style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)` }}
          >
            <TrendingUp className="w-3 h-3" strokeWidth={2.6} />
            {chip.score}
          </div>
        </div>

        {/* Quote */}
        <p className="text-[14px] text-lm-text leading-snug line-clamp-4">
          &ldquo;{chip.text}&rdquo;
        </p>

        {/* Intent footer */}
        <div className="mt-3 flex items-center gap-1.5">
          <Zap className="w-3.5 h-3.5" fill={accent} stroke={accent} strokeWidth={2} />
          <span
            className="text-[10px] font-mono font-semibold uppercase tracking-wider"
            style={{ color: accent }}
          >
            {chip.intent}
          </span>
        </div>
      </div>
    </div>
  );
}

// A single dimmed "peek" card used behind the primary column to create the deck illusion.
function GhostChip({ chip, accent, accentSoft }) {
  return (
    <div
      className="rounded-lg bg-white/85 p-3 shadow-[0_12px_28px_-18px_rgba(0,0,0,0.28)]"
      style={{ boxShadow: `0 14px 30px -18px ${accent}44` }}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <SourceLogo source={chip.source} className="w-3.5 h-3.5 shrink-0" />
          <span className="text-[10px] font-semibold text-lm-text truncate">
            {chip.handle}
          </span>
        </div>
        <div
          className="flex items-center gap-0.5 text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded-md text-white shrink-0"
          style={{ background: `linear-gradient(135deg, ${accent}, ${accentSoft})` }}
        >
          <TrendingUp className="w-2.5 h-2.5" strokeWidth={2.6} />
          {chip.score}
        </div>
      </div>
      <p className="text-[11px] text-lm-text3 leading-snug line-clamp-2">comment…</p>
      <div className="mt-2 flex items-center gap-1">
        <Zap className="w-2.5 h-2.5" fill={accent} stroke={accent} strokeWidth={2} />
        <span className="text-[9px] font-mono font-semibold uppercase" style={{ color: accent }}>
          Tag
        </span>
      </div>
    </div>
  );
}

export default function PersonasTabs() {
  const [active, setActive] = useState(0);
  const p = PERSONAS[active];
  const [ref, visible] = useReveal();

  return (
    <section
      id="use-cases"
      className="relative py-20 md:py-28 px-5 md:px-10 lm-cv overflow-hidden bg-lm-bg"
    >
      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-12 md:mb-16 lm-reveal ${visible ? "is-visible" : ""}`}
        >
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-lm-secondary mb-4 px-3 py-1 rounded-full bg-lm-secondary/10 border border-lm-secondary/20">
            <Sparkles className="w-3 h-3" strokeWidth={2.4} />
            For whom
          </div>
          <h2 className="text-4xl md:text-6xl font-display font-semibold leading-[1.05] tracking-tight text-lm-text max-w-3xl mx-auto">
            Built for{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(120deg, #0F2A1F 0%, #A83820 100%)" }}>
                busy Indian founders
              </span>
              <span className="absolute left-0 right-0 bottom-1 h-2 bg-lm-secondary/25 -z-0 rounded" aria-hidden />
            </span>
          </h2>
          <p className="mt-5 text-lg text-lm-text2 leading-relaxed max-w-2xl mx-auto">
            Price-sensitive, time-poor, not technical - and tired of tools that need onboarding.
          </p>
          <p className="mt-3 text-xs text-lm-text3 italic max-w-2xl mx-auto">
            Signal chips below are illustrative examples of what Leadmato surfaces — not real users or handles.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-10">
          {PERSONAS.map((persona, i) => {
            const Ic = persona.icon;
            const isActive = i === active;
            return (
              <button
                key={persona.key}
                onClick={() => setActive(i)}
                data-testid={`persona-tab-${persona.key}`}
                className={`relative group inline-flex items-center gap-2 px-4 md:px-5 py-2.5 md:py-3 rounded-full text-sm font-semibold transition-all ${
                  isActive
                    ? "text-white shadow-[0_14px_30px_-14px_rgba(0,0,0,0.4)] -translate-y-0.5"
                    : "text-lm-text2 bg-white border border-lm-border hover:border-lm-primary/30 hover:-translate-y-0.5"
                }`}
                style={
                  isActive
                    ? {
                        background: `linear-gradient(135deg, ${persona.accent}, ${persona.accentSoft})`,
                      }
                    : undefined
                }
              >
                <span
                  className={`w-6 h-6 rounded-md flex items-center justify-center ${
                    isActive ? "bg-white/20 text-white" : "bg-lm-tint2 text-lm-primary"
                  }`}
                >
                  <Ic className="w-3.5 h-3.5" strokeWidth={2.4} />
                </span>
                <span>{persona.tab}</span>
              </button>
            );
          })}
        </div>

        {/* Spotlight card */}
        <div
          className="relative max-w-5xl mx-auto rounded-lg overflow-hidden border border-black/5"
          style={{
            background: p.mesh,
            transition: "background 0.6s ease",
          }}
          key={`spotlight-${p.key}`}
        >
          {/* Fine grain overlay */}
          <div
            className="absolute inset-0 opacity-[0.035] mix-blend-multiply pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/%3E%3C/svg%3E\")",
            }}
            aria-hidden="true"
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-10 p-8 md:p-14">
            {/* LEFT - narrative */}
            <div className="flex flex-col justify-center relative">
              {/* Persona index */}
              <div className="flex items-center gap-3 mb-6">
                <span
                  className="text-[64px] md:text-[88px] font-display font-bold leading-none bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(180deg, ${p.accent} 0%, ${p.accentSoft} 100%)`,
                  }}
                >
                  0{active + 1}
                </span>
                <span className="text-sm font-mono text-lm-text3 tracking-wider">
                  / 0{PERSONAS.length}
                </span>
              </div>

              {/* Persona chip */}
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60 backdrop-blur-sm border border-white/80 self-start mb-4"
                style={{ boxShadow: `0 6px 20px -8px ${p.accent}44` }}
              >
                <span
                  className="w-6 h-6 rounded-md flex items-center justify-center text-white"
                  style={{ background: `linear-gradient(135deg, ${p.accent}, ${p.accentSoft})` }}
                >
                  <p.icon className="w-3.5 h-3.5" strokeWidth={2.4} />
                </span>
                <span className="text-sm font-semibold text-lm-text">{p.label}</span>
                <span className="text-[11px] font-mono text-lm-text3">· {p.tagline}</span>
              </div>

              <h3 className="text-3xl md:text-4xl lg:text-[42px] font-display font-semibold tracking-tight leading-[1.1] text-lm-text">
                {p.title}
              </h3>
              <p className="mt-4 text-lm-text2 leading-relaxed max-w-lg">{p.subtitle}</p>

              {/* Perks */}
              <ul className="mt-6 space-y-2.5">
                {p.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2.5 text-[15px] text-lm-text">
                    <span
                      className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-white shrink-0"
                      style={{
                        background: `linear-gradient(135deg, ${p.accent}, ${p.accentSoft})`,
                      }}
                    >
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a
                  href="#pricing"
                  className="inline-flex items-center gap-2 text-white text-sm font-semibold px-5 py-3 rounded-lg transition-all hover:-translate-y-0.5"
                  style={{
                    background: `linear-gradient(135deg, ${p.accent}, ${p.accentSoft})`,
                    boxShadow: `0 12px 26px -12px ${p.accent}99`,
                  }}
                >
                  Get started
                  <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                </a>
                <span className="text-xs text-lm-text3 font-mono">Your first scan is free · 10-minute setup</span>
              </div>
            </div>

            {/* RIGHT - layered card deck */}
            <div className="relative min-h-[420px] lg:min-h-[520px]">
              {/* Back column - most offset, lowest opacity */}
              <div
                className="pointer-events-none absolute top-14 right-[-40px] w-40 flex flex-col gap-2.5 opacity-45"
                aria-hidden
              >
                {[...p.chips, ...p.chips].slice(0, 5).map((c, i) => (
                  <GhostChip
                    key={`ghost-back-${p.key}-${i}`}
                    chip={{ ...c, handle: `r/Community${i + 1}`, score: [76, 8, 62, 79, 40][i] }}
                    accent={p.accent}
                    accentSoft={p.accentSoft}
                  />
                ))}
              </div>

              {/* Mid column - slight offset, medium opacity */}
              <div
                className="pointer-events-none absolute top-6 right-[40px] w-52 flex flex-col gap-2.5 opacity-70"
                aria-hidden
              >
                {[...p.chips, ...p.chips].slice(0, 5).map((c, i) => (
                  <GhostChip
                    key={`ghost-mid-${p.key}-${i}`}
                    chip={{
                      ...c,
                      handle:
                        i === 0
                          ? c.handle
                          : `${c.source === "Forums" ? "Forum user" : c.source === "LinkedIn" ? "LinkedIn user" : `r/Signal${i + 3}`}`,
                      score: [76, 62, 0, 40, 12][i],
                    }}
                    accent={p.accent}
                    accentSoft={p.accentSoft}
                  />
                ))}
              </div>

              {/* Front column - 3 primary signal chips */}
              <div className="relative ml-auto w-[280px] md:w-[320px] flex flex-col gap-3.5">
                {p.chips.map((c, i) => (
                  <SignalChip
                    key={`front-${p.key}-${i}`}
                    chip={c}
                    idx={i}
                    accent={p.accent}
                    accentSoft={p.accentSoft}
                  />
                ))}
              </div>

              {/* Sparkle decoration bottom-right */}
              <div
                className="absolute bottom-2 right-2 opacity-60"
                aria-hidden
                style={{ color: p.accent }}
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l1.4 4.6L18 8l-4.6 1.4L12 14l-1.4-4.6L6 8l4.6-1.4L12 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
